import React, {FC, useEffect} from 'react'

import cn from 'classnames/bind'
import dynamic from 'next/dynamic'

import FormW2 from 'components/blocks/forms/FormW2'
import LandingsTitle from 'components/blocks/LandingsTitle'
import W2Faq from 'components/blocks/w2/W2Faq/W2Faq'
import W2Filling from 'components/blocks/w2/W2Filling/W2Filling'
import W2FormContent from 'components/blocks/w2/W2FormContent/W2FormContent'
import W2HowItWorks from 'components/blocks/w2/W2HowItWorks/W2HowItWorks'
import W2HowToGet from 'components/blocks/w2/W2HowToGet/W2HowToGet'
import W2Mistakes from 'components/blocks/w2/W2Mistakes/W2Mistakes'
import W2Penalties from 'components/blocks/w2/W2Penalties/W2Penalties'
import W2Testimonials from 'components/blocks/w2/W2Testimonials/W2Testimonials'
import W2TipsWhenLosing from 'components/blocks/w2/W2TipsWhenLosing/W2TipsWhenLosing'
import W2TipsWhenSeveral from 'components/blocks/w2/W2TipsWhenSeveral/W2TipsWhenSeveral'
import W2Users from 'components/blocks/w2/W2Users/W2Users'
import W2What from 'components/blocks/w2/W2What/W2What'
import W2Why from 'components/blocks/w2/W2Why/W2Why'
import {Container, Row, Col} from 'components/elements/grid'
import LoadingComponent from 'components/elements/LoadingComponent'
import {W2Page} from 'src/types/__sanitySchema__'
import {posthogTrack, klaviyoTrack} from 'src/utils'
import {useRootStore} from 'src/utils/contexts/RootStore.context'
import {useToggleState} from 'src/utils/hooks/useToggleState'

import s from './TemplateCreateDocumentW2.module.scss'

const DynamicW2DocumentPreviews = dynamic(
	() => import('components/blocks/documents/DocumentW2/previews/W2DocumentPreviews'),
	{loading: () => <LoadingComponent containerHeight={800} />},
)
const cx = cn.bind(s)

interface ITemplateCreateDocumentInvoiceProps {
	content: W2Page
	className?: string
}

const TemplateCreateDocumentW2: FC<ITemplateCreateDocumentInvoiceProps> = ({
	className,
	content,
}) => {
	const store = useRootStore()

	const [isPreview, togglePreview] = useToggleState(false)

	useEffect(() => {
		posthogTrack('[W2 FORM] Page opened')
		klaviyoTrack('Page opened', {page: 'w2', href: window?.location?.href})
	}, [])

	useEffect(() => {
		if (isPreview) {
			posthogTrack('[W2 FORM] Document preview opened')
		}
	}, [isPreview])

	return (
		<div className={cx('wrapper', className, {'wrapper--preview-mode': isPreview})}>
			<Container>
				<Row>
					<Col lg={{span: 10, offset: 1}} xl={{span: 8, offset: 2}}>
						<LandingsTitle
							type="top"
							className={s.title}
							topClassName={s.titleTop}
							title={content.title}
							subtitle={content.subtitle}
						/>
					</Col>
				</Row>
			</Container>
			{isPreview ? (
				<DynamicW2DocumentPreviews
					togglePreview={togglePreview}
					isPreviewOnly
					data={store.w2.form}
				/>
			) : (
				<div>
					<FormW2 togglePreview={togglePreview} />

					<W2Why className={cx('why')} content={content.why} />
					<W2What className={cx('section', 'what')} content={content.what} />
					<W2HowToGet className={cx('section', 'how-to-get')} content={content.howToGet} />
					<W2HowItWorks className={cx('section', 'how-it-works')} content={content.howItWorks} />
					<W2FormContent className={cx('section', 'form-content')} content={content.formContent} />
					<W2Users className={cx('section', 'users')} content={content.users} />
					<W2Testimonials
						className={cx('section', 'testimonials')}
						content={content.testimonials}
					/>
					<W2Mistakes className={cx('section', 'mistakes')} content={content.mistakes} />
					<W2TipsWhenSeveral
						className={cx('section', 'tips-when-several')}
						content={content.tipsWhenSeveral}
					/>
					<W2TipsWhenLosing
						className={cx('section', 'tips-when-losing')}
						content={content.tipsWhenLosing}
					/>
					<W2Penalties className={cx('section', 'penalties')} content={content.penalties} />
					<W2Filling className={cx('section', 'filling')} content={content.filling} />
					<W2Faq className={cx('section', 'faq')} content={content.faq} />
				</div>
			)}
		</div>
	)
}

TemplateCreateDocumentW2.displayName = 'TemplateCreateDocumentW2'

export default TemplateCreateDocumentW2
